'use client';
import { createContext, useEffect, useRef, useState } from 'react';
import { useContextStrict } from '@web-libs/core/context/context.functions';
import { AuthRepository } from '@web-libs/features/auth/main/domain/auth.repository';
import useAuthCookies from '@web-libs/features/auth/storage/use-auth-storage.concern';
import { UseTageaRouter } from '@web-libs/core/routing/application/use-tagea-router.concern';
import { routes } from '@web-app/[lang]/[tenant]/[institution]/routes-institution';
import { RegisterFormInputs } from '@web-libs/features/auth/register/application/register-form.concern';
import {
  AnonymousRegistration,
  LoginRequestBranding,
  NormalRegistration,
  PasswordResetPayload,
} from '@web-libs/features/auth/main/domain/auth.types';
import { createBirthday } from '@web-libs/core/shared-domain/values/birthday';
import Cookies from 'js-cookie';
import {
  EmergencyCookieName,
  EmergencyCookieValue,
} from '@web-libs/features/auth/main/domain/auth.constants';
import { AuthV2Service } from '@tremaze/shared/core/auth-v2';
import { firstValueFrom, Subscription } from 'rxjs';

export interface AuthConcern {
  setAuthService: (service: AuthV2Service) => void;
  login: (code: string) => Promise<boolean>;
  requestLogin: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
  logout: () => void;
  emergencyLogout: () => void;
  register: (formInputs: RegisterFormInputs) => Promise<boolean>;
  resetPassword: (payload: PasswordResetPayload) => Promise<boolean>;

  registerOnTokenChange(onTokenChange: OnTokenChange): void;

  removeOnTokenChange(onTokenChange: OnTokenChange): void;

  getAuthToken(): string | null;

  authToken: string | null;
  isAuthenticated: boolean | null;
  checkUsernameUsed: (username: string) => Promise<boolean>;
  checkEmailUsed: (email: string) => Promise<boolean>;
}

export const AuthContext = createContext<AuthConcern | null>(null);

export const useAuth = () => useContextStrict(AuthContext);

export type OnTokenChange = (token: string | null) => void;
export const useAuthCreation = (
  authRepository: AuthRepository,
  onTokenChange: OnTokenChange,
  loginRequestBranding: LoginRequestBranding,
  router: UseTageaRouter,
): AuthConcern => {
  const onTokenChangeListeners = useRef<OnTokenChange[]>([onTokenChange]);
  const { saveAuthToken, removeAuthToken } = useAuthCookies();

  const registerOnTokenChange = (onTokenChange: OnTokenChange) => {
    onTokenChangeListeners.current = [
      ...onTokenChangeListeners.current,
      onTokenChange,
    ];
  };

  const removeOnTokenChange = (onTokenChange: OnTokenChange) => {
    onTokenChangeListeners.current = onTokenChangeListeners.current.filter(
      (listener) => listener !== onTokenChange,
    );
  };

  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  useEffect(() => {
    return () => {
      if (authTokenSubscription.current) {
        authTokenSubscription.current.unsubscribe();
      }
      if (authenticatedSubscription.current) {
        authenticatedSubscription.current.unsubscribe();
      }
    };
  }, []);

  const authTokenSubscription = useRef<Subscription | null>(null);
  const authenticatedSubscription = useRef<Subscription | null>(null);
  const authServiceRef = useRef<AuthV2Service | null>(null);

  return {
    setAuthService: (service: AuthV2Service) => {
      authServiceRef.current = service;
      if (authTokenSubscription.current) {
        return;
      }
      authTokenSubscription.current = service.accessToken$.subscribe(
        (token) => {
          onTokenChangeListeners.current.forEach((listener) => listener(token));
          saveAuthToken(token);
          setAuthToken(token);
        },
      );
      authenticatedSubscription.current = service.authenticated$.subscribe(
        (isAuthenticated) => {
          setIsAuthenticated(isAuthenticated);
        },
      );
    },
    async requestLogin(): Promise<void> {
      if (!authServiceRef.current) {
        throw new Error(
          'AuthService is not defined. Please provide an AuthService to the useAuthCreation function.',
        );
      }
      authServiceRef.current.login();
    },
    login: async (code: string) => {
      const authResponse = await authRepository.login(code);
      saveAuthToken(authResponse.access_token);
      return true;
    },
    refreshToken: async () => {
      if (!authServiceRef.current) {
        throw new Error(
          'AuthService is not defined. Please provide an AuthService to the useAuthCreation function.',
        );
      }
      const refreshedToken = await firstValueFrom(
        authServiceRef.current?.getRefreshedToken(),
      );
      if (!refreshedToken) {
        return null;
      }
      saveAuthToken(refreshedToken);
      return authToken;
    },
    logout: async () => {
      authServiceRef.current?.logout();
      removeAuthToken();
      router.push(routes.landing);
    },
    emergencyLogout: () => {
      removeAuthToken();
      Cookies.set(EmergencyCookieName, EmergencyCookieValue);
      window.location.replace('https://www.google.de/?hl=de');
    },
    register: async (formInputs: RegisterFormInputs) => {
      const instId = router.getInsitutionId();
      if (formInputs.anonymous) {
        if (!formInputs.userName) {
          throw new Error(
            'Username was undefined, but anonymous was true in Registration.',
          );
        }
        const registration: AnonymousRegistration = {
          username: formInputs.userName,
          password: formInputs.password,
          institutionId: instId,
          specializationId: formInputs.specializationId,
        };
        return await authRepository.registerAnonymous(registration);
      } else {
        if (
          !formInputs.firstName ||
          !formInputs.lastName ||
          !formInputs.email
        ) {
          throw new Error(
            'First name, last name or email was undefined, but anonymous was false in Registration.',
          );
        }
        if (!formInputs.birth) {
          throw new Error(
            'Birthday was undefined, but anonymous was false in Registration.',
          );
        }
        const registration: NormalRegistration = {
          firstName: formInputs.firstName,
          lastName: formInputs.lastName,
          email: formInputs.email,
          birthday: createBirthday(formInputs.birth),
          password: formInputs.password,
          institutionId: instId,
          mobile: formInputs.mobile,
          specializationId: formInputs.specializationId,
        };
        return await authRepository.register(registration);
      }
    },
    checkEmailUsed: async (email: string) => {
      return await authRepository.checkEmailUsed(email);
    },
    checkUsernameUsed: async (username: string) => {
      return await authRepository.checkUsernameUsed(username);
    },
    resetPassword: async (payload: PasswordResetPayload) => {
      return await authRepository.resetPassword(payload);
    },
    registerOnTokenChange,
    removeOnTokenChange,
    getAuthToken: () => authToken,
    authToken,
    isAuthenticated,
  };
};
